import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { useShowToast } from '@chilipiper/design-system/src/new'
import { useApiClient } from './apiClientContext'

// Must match `userQueryKey` in @chilipiper/fire-session — FireSessionProvider fetches
// this exact request on boot, so sharing the key avoids a second identical call.
const userQueryKey = (tenantId: string, userId: string) => ['users', tenantId, userId, 'v3']
export const useUser = (
  options: { enabled?: boolean; suspense?: boolean; userId?: string } = {}
) => {
  const { workspaceV3 } = useApiClient()
  const { userId, tenantId } = useFireSessionContext()
  const id = options.userId ?? userId

  return useQuery({
    queryKey: userQueryKey(tenantId, id),
    queryFn: () =>
      workspaceV3
        .findUsersByFilterV3(tenantId, { userIds: [id], licenses: {} }, { pageSize: 1 })
        .then(res => res.results?.[0] ?? null),
    ...options,
  })
}

export const useFinishOnboarding = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const queryKey = userQueryKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: () =>
      workspaceV3.updateOwnOnboardingStatusV3(tenantId, { onboardingStatus: 'Onboarded' }),
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData(queryKey, oldData => {
        if (oldData) {
          return {
            ...oldData,
            onboardingStatus: 'Onboarded' as const,
          }
        }
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}

const tenantQueryKey = (tenantId: string) => ['tenant', tenantId]
export const useTenant = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantid(tenantId),
  })
}

export const useAcceptInvite = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const queryKey = userQueryKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: () => workspaceV3.acceptInviteV3(tenantId, userId),
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData(queryKey, oldData => {
        if (oldData) {
          return {
            ...oldData,
            invitedAt: undefined,
          }
        }
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}

export const useDeclineInvite = () => {
  const { tenantId, userId } = useFireSessionContext()
  const queryClient = useQueryClient()
  const queryKey = userQueryKey(tenantId, userId)
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: () => workspaceV3.declineInviteV3(tenantId, userId),
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData(queryKey, oldData => {
        if (oldData) {
          return {
            ...oldData,
            invitedAt: undefined,
          }
        }
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}

// Same request as useTenant, so it shares that cache entry instead of fetching twice.
export const useOrganizationDetails = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => workspace.getApiV1TenantTenantid(tenantId),
  })
}

export const useRequestLicense = () => {
  const { tenantId, userId } = useFireSessionContext()
  const showToast = useShowToast()
  const { workspaceV3 } = useApiClient()

  return useMutation({
    mutationFn: () =>
      workspaceV3.sendRequestLicenseNotificationV3(tenantId, {
        userId: userId,
        license: 'ChiliCalOrg',
      }),
    onError: () => {
      showToast({
        title: 'Failed to request a license',
        variant: 'caution',
      })
    },
  })
}
